const SUDS_MAX = 10;
const EMERGENCY_THRESHOLD = 7;
function sudsColor(value, colors) {
  if (value >= EMERGENCY_THRESHOLD) return colors.suds_hot_red;
  if (value >= 4) return colors.suds_mid_yellow;
  return colors.suds_cold_blue;
}
function sudsLabel(value) {
  if (value >= 9) return "Crisis / overwhelming";
  if (value >= 7) return "High distress";
  if (value >= 4) return "Moderate distress";
  if (value >= 1) return "Mild / manageable";
  return "Calm";
}
const FOUR_F = [
  {
    key: "fight",
    label: "Fight",
    blurb: "Anger, confrontation, urge to defend or lash out.",
    colorKey: "state_fight",
    icon: "flame"
  },
  {
    key: "flight",
    label: "Flight",
    blurb: "Restless, needing to escape, avoid or run.",
    colorKey: "state_flight",
    icon: "wind"
  },
  {
    key: "freeze",
    label: "Freeze",
    blurb: "Numb, stuck, spaced-out or unable to move.",
    colorKey: "state_freeze",
    icon: "snowflake"
  },
  {
    key: "fawn",
    label: "Fawn",
    blurb: "People-pleasing, appeasing, over-apologising.",
    colorKey: "state_fawn",
    icon: "heart"
  }
];
function fourFConfig(key) {
  return FOUR_F.find((f) => f.key === key);
}
const FOURF_SOOTHE = {
  fight: "Discharge the surge safely \u2014 clench your fists tight for five seconds, then release. Let your out-breath be longer than your in-breath.",
  flight: "You don't have to run. Feel your feet flat on the ground and slow your out-breath \u2014 you are allowed to stay right here.",
  freeze: "Gently wake your body \u2014 wiggle your fingers and toes, roll your shoulders, and name one thing you can hear right now.",
  fawn: "Turn toward yourself. Ask quietly: \u201CWhat do I need right now?\u201D and let your own needs matter as much as anyone else's."
};
function fourFSoothe(key) {
  if (!key) return null;
  return FOURF_SOOTHE[key] ?? null;
}
const DECISION_TREE = [
  {
    id: "q_fight",
    question: "Did you feel a surge of anger, or an urge to confront, argue, or push back?",
    yes: "fight"
  },
  {
    id: "q_flight",
    question: "Did you feel an urge to escape, avoid, or get away \u2014 restless or needing to run?",
    yes: "flight"
  },
  {
    id: "q_freeze",
    question: "Did you feel frozen, numb, stuck, or spaced-out \u2014 unable to move or speak?",
    yes: "freeze"
  },
  {
    id: "q_fawn",
    question: "Did you find yourself people-pleasing, appeasing, or putting others first to feel safe?",
    yes: "fawn"
  }
];
const OVERRIDE_TOOL = "Triggerific override";
const VALIDATION_TOOL = "Being validated by Triggerific";
const COPING_OPTIONS = [
  OVERRIDE_TOOL,
  VALIDATION_TOOL,
  "Cold water / ice (TIP)",
  "Paced breathing",
  "Grounding (5-4-3-2-1)",
  "Movement / exercise",
  "Left the situation",
  "Reached out to someone",
  "Journaling",
  "Music / soundscape",
  "Self-soothing touch",
  "Prayer / meditation",
  "Distraction",
  "Medication"
];
const SCHEMA_START = "age";
const SCHEMA_TREE = {
  age: {
    id: "age",
    question: "What age do I feel right now?",
    options: [
      { label: "Adult (\u226518)", next: "adult_critical" },
      { label: "Child (<18)", next: "child_anger" }
    ]
  },
  adult_critical: {
    id: "adult_critical",
    question: "Am I being critical of myself?",
    options: [
      { label: "Yes", next: "adult_punished" },
      { label: "No", result: "Healthy Adult" }
    ]
  },
  adult_punished: {
    id: "adult_punished",
    question: "Do I feel I should be punished?",
    options: [
      { label: "Yes", result: "The Punitive Critic" },
      { label: "No", next: "adult_burden" }
    ]
  },
  adult_burden: {
    id: "adult_burden",
    question: "Do I feel like a burden?",
    options: [
      { label: "Yes", result: "Guilt-Inducing Critic" },
      { label: "No", result: "Demanding Critic" }
    ]
  },
  child_anger: {
    id: "child_anger",
    question: "Do I feel anger?",
    options: [
      { label: "Yes", next: "child_express" },
      { label: "No", next: "child_disconnect" }
    ]
  },
  child_express: {
    id: "child_express",
    question: "Am I expressing this anger, or feeling overwhelmed?",
    options: [
      { label: "Expressing", result: "Enraged Child" },
      { label: "Overwhelmed", result: "Angry Child" }
    ]
  },
  child_disconnect: {
    id: "child_disconnect",
    question: "Do I feel disconnected from others?",
    options: [
      { label: "Yes", next: "child_impulsive" },
      { label: "No", result: "Happy Child" }
    ]
  },
  child_impulsive: {
    id: "child_impulsive",
    question: "Do I feel impulsive?",
    options: [
      { label: "Yes", result: "Impulsive Child" },
      { label: "No", next: "child_overwhelmed" }
    ]
  },
  child_overwhelmed: {
    id: "child_overwhelmed",
    question: "Do I feel overwhelmed?",
    options: [
      { label: "Yes", result: "Vulnerable Child" },
      { label: "No", result: "Undisciplined Child" }
    ]
  }
};
function isSchemaResult(o) {
  return "result" in o;
}
const SCHEMA_MEANINGS = {
  "Healthy Adult": "The calm, grown-up part of you that can hold feelings with balance and care.",
  "The Punitive Critic": "A harsh inner voice that believes you deserve punishment for falling short.",
  "Guilt-Inducing Critic": "An inner voice that makes you feel like a burden whenever you have needs.",
  "Demanding Critic": "An inner voice pushing you to do more and be more \u2014 never quite enough.",
  "Enraged Child": "A young part of you letting out big anger that once had nowhere safe to go.",
  "Angry Child": "A young part feeling anger it can't hold \u2014 overwhelmed and unheard.",
  "Happy Child": "The playful, open part of you that feels safe, connected and free.",
  "Impulsive Child": "A young part acting on urges in the moment to feel something other than pain.",
  "Vulnerable Child": "The tender, frightened part of you that feels small, alone and in need of comfort.",
  "Undisciplined Child": "A young part that struggles to stay with hard or boring things right now."
};
const SCHEMA_SOOTHE = {
  "Healthy Adult": "Place a hand on your heart and notice how steady you feel right now. Rest here for three slow breaths.",
  "The Punitive Critic": "Speak to yourself as you would a good friend: \u201CYou made a mistake, not a crime.\u201D Soften your jaw and unclench your hands.",
  "Guilt-Inducing Critic": "Say quietly: \u201CMy needs are allowed to take up space.\u201D Take one slow breath just for yourself.",
  "Demanding Critic": "Let one thing be good enough right now. Drop your shoulders and breathe out slowly for a count of six.",
  "Enraged Child": "Let the energy move safely \u2014 press your palms hard against a wall for ten seconds, then release and shake out your arms.",
  "Angry Child": "Name it out loud: \u201CI feel angry, and that's okay.\u201D Press your feet firmly into the floor and breathe.",
  "Happy Child": "Stay with this lightness. Notice one thing around you that makes you smile and let yourself enjoy it fully.",
  "Impulsive Child": "Pause before acting. Sip some cold water slowly and count five things you can see before you decide anything.",
  "Vulnerable Child": "Wrap your arms around yourself gently. Whisper: \u201CYou're safe now, I've got you,\u201D and breathe slow and low.",
  "Undisciplined Child": "Choose one tiny, kind next step. Set a two-minute timer and let yourself do only that."
};
function schemaMeaning(state) {
  if (!state) return null;
  return SCHEMA_MEANINGS[state] ?? null;
}
function schemaSoothe(state) {
  if (!state) return null;
  return SCHEMA_SOOTHE[state] ?? null;
}
const COPING_MODE_START = "urge_fight";
const COPING_MODE_TREE = {
  // Master router
  urge_fight: {
    id: "urge_fight",
    question: "Do I feel an internal urge to fight something or someone?",
    options: [
      { label: "Yes", next: "oc_lash" },
      { label: "No", next: "escape" }
    ]
  },
  escape: {
    id: "escape",
    question: "Do I feel the need to escape?",
    options: [
      { label: "Yes", next: "av_protect" },
      { label: "No", next: "su_rescue" }
    ]
  },
  // Branch 1: Overcompensator (Fight)
  oc_lash: {
    id: "oc_lash",
    question: "Do I feel the urge to lash out at something or someone?",
    options: [
      { label: "Yes", next: "oc_aggressive" },
      { label: "No", next: "oc_control" }
    ]
  },
  oc_aggressive: {
    id: "oc_aggressive",
    question: "Am I being aggressive?",
    options: [
      { label: "Yes", next: "oc_plan" },
      { label: "No", result: "Coping Manipulator" }
    ]
  },
  oc_plan: {
    id: "oc_plan",
    question: "Do I need to plan my attack?",
    options: [
      { label: "Yes", result: "Predator" },
      { label: "No", result: "Bully Attack" }
    ]
  },
  oc_control: {
    id: "oc_control",
    question: "Do I need to control the situation?",
    options: [
      { label: "Yes", next: "oc_trust" },
      { label: "No", next: "oc_isolated" }
    ]
  },
  oc_trust: {
    id: "oc_trust",
    question: "Do I have issues with trust?",
    options: [
      { label: "Yes", next: "oc_hyperalert" },
      { label: "No", next: "oc_approval" }
    ]
  },
  oc_hyperalert: {
    id: "oc_hyperalert",
    question: "Am I hyper-alert to danger?",
    options: [
      { label: "Yes", result: "Suspicious Overcontroller" },
      { label: "No", result: "Perfectionist Overcontroller" }
    ]
  },
  oc_approval: {
    id: "oc_approval",
    question: "Do I need approval?",
    options: [
      { label: "Yes", result: "Approval/Recognition Seeker" },
      { label: "No", next: "oc_blame" }
    ]
  },
  oc_blame: {
    id: "oc_blame",
    question: "Am I blaming myself?",
    options: [
      { label: "Yes", result: "Flagellating Overcontroller" },
      { label: "No", result: "Scolding Overcontroller" }
    ]
  },
  oc_isolated: {
    id: "oc_isolated",
    question: "Do I feel isolated?",
    options: [
      { label: "Yes", result: "Angry Protector" },
      { label: "No", next: "oc_ruminating" }
    ]
  },
  oc_ruminating: {
    id: "oc_ruminating",
    question: "Am I ruminating?",
    options: [
      { label: "Yes", result: "Overanalysing Overcontroller" },
      { label: "No", result: "Self-Aggrandiser" }
    ]
  },
  // Branch 2: Avoidance (Flight)
  av_protect: {
    id: "av_protect",
    question: "Do I need to protect myself?",
    options: [
      { label: "Yes", next: "av_autopilot" },
      { label: "No", result: "Detached Self-Soother" }
    ]
  },
  av_autopilot: {
    id: "av_autopilot",
    question: "Am I on autopilot?",
    options: [
      { label: "Yes", result: "Detached Protector" },
      { label: "No", result: "Avoidant Protector" }
    ]
  },
  // Branch 3: Surrender (Freeze)
  su_rescue: {
    id: "su_rescue",
    question: "Do I need rescuing?",
    options: [
      { label: "Yes", result: "Helpless Surrenderer" },
      { label: "No", next: "su_victim" }
    ]
  },
  su_victim: {
    id: "su_victim",
    question: "Do I feel like a victim?",
    options: [
      { label: "Yes", result: "Self-Pity Victim" },
      { label: "No", result: "Compliant Surrenderers" }
    ]
  }
};
function isModeResult(o) {
  return "result" in o;
}
const MODE_FOURF = {
  // Overcompensator (Fight)
  Predator: "fight",
  "Bully Attack": "fight",
  "Coping Manipulator": "fight",
  "Suspicious Overcontroller": "fight",
  "Perfectionist Overcontroller": "fight",
  "Approval/Recognition Seeker": "fight",
  "Flagellating Overcontroller": "fight",
  "Scolding Overcontroller": "fight",
  "Angry Protector": "fight",
  "Overanalysing Overcontroller": "fight",
  "Self-Aggrandiser": "fight",
  // Avoidance (Flight)
  "Detached Protector": "flight",
  "Avoidant Protector": "flight",
  "Detached Self-Soother": "flight",
  // Surrender (Freeze)
  "Helpless Surrenderer": "freeze",
  "Self-Pity Victim": "freeze",
  "Compliant Surrenderers": "freeze"
};
function modeFourF(mode) {
  if (!mode) return null;
  return MODE_FOURF[mode] ?? null;
}
const MODE_MEANINGS = {
  Predator: "A cold, calculated push to remove a threat before it can hurt you.",
  "Bully Attack": "Using force or intimidation to feel powerful and keep others at bay.",
  "Coping Manipulator": "Getting your needs met through indirect control, charm or deception.",
  "Self-Aggrandiser": "Feeling special or superior to cover a deeper sense of not being enough.",
  "Suspicious Overcontroller": "Scanning constantly for threat or betrayal \u2014 controlling through vigilance and mistrust.",
  "Flagellating Overcontroller": "Turning harsh control inward, punishing yourself to pre-empt failure or blame.",
  "Scolding Overcontroller": "A stern, critical inner voice that tries to keep you in line through disapproval.",
  "Overanalysing Overcontroller": "Over-thinking and dissecting everything to stay in control and avoid being caught off guard.",
  "Approval/Recognition Seeker": "Chasing praise or status to feel worthy and seen.",
  "Detached Self-Soother": "Numbing or distracting yourself with soothing activities to avoid feeling.",
  "Detached Protector": "Switching off emotionally \u2014 going numb or blank to stay safe.",
  "Avoidant Protector": "Withdrawing from situations that might stir painful feelings.",
  "Angry Protector": "Using a wall of anger to keep people at a safe distance.",
  "Perfectionist Overcontroller": "Controlling everything through high standards to stop things going wrong.",
  "Invisible / Hyper-autonomous Overcontroller": "Staying hidden and fiercely self-reliant so no one can let you down.",
  "Helpless Surrenderer": "Giving in and feeling powerless, waiting for someone to rescue you.",
  "Self-Pity Victim": "Sinking into feeling wronged and hard-done-by as a way to cope.",
  "Compliant Surrenderers": "Going along with others' wishes to avoid conflict or rejection."
};
function modeMeaning(mode) {
  if (!mode) return null;
  return MODE_MEANINGS[mode] ?? null;
}
const MODE_SOOTHE = {
  Predator: "You're not in danger right now. Unclench your hands, feel your feet on the floor, and let your breath slow \u2014 this can be met from a calmer place.",
  "Bully Attack": "Notice the urge to overpower, then let it soften. Drop your shoulders, breathe out slowly \u2014 you can be strong without force.",
  "Coping Manipulator": "Pause and get honest with yourself about what you actually need. Take one slow breath and name it plainly.",
  "Self-Aggrandiser": "Underneath the need to feel special is a tender part. Place a hand on your heart and offer it warmth \u2014 you're enough as you are.",
  "Suspicious Overcontroller": "Right now, in this moment, you are safe. Soften your gaze, unclench your jaw, and let one slow breath tell your body the danger has passed.",
  "Flagellating Overcontroller": "You don't have to punish yourself to stay safe. Place a hand on your heart and offer the kindness you'd give a struggling friend.",
  "Scolding Overcontroller": "Notice the harsh inner voice, then answer it gently. Breathe out slowly \u2014 you're allowed to be human and still be okay.",
  "Overanalysing Overcontroller": "You don't have to figure it all out right now. Drop out of your head and into your breath \u2014 feel your feet, and let the thinking rest.",
  "Approval/Recognition Seeker": "Your worth isn't up for a vote. Breathe in, and offer yourself the recognition you're seeking.",
  "Detached Self-Soother": "Gently come back into your body \u2014 sip some water, feel your feet, and let one real feeling be here for a moment.",
  "Detached Protector": "It's safe to feel a little now. Wiggle your fingers, take a slow breath, and let yourself thaw gently at your own pace.",
  "Avoidant Protector": "Notice what you're moving away from. Take one grounding breath \u2014 you can face it in small, kind steps.",
  "Angry Protector": "The anger is guarding something tender. Soften your jaw, breathe out slowly, and let a little safety in.",
  "Perfectionist Overcontroller": "Let one thing be good enough right now. Loosen your grip, drop your shoulders, and breathe out for a slow count of six.",
  "Invisible / Hyper-autonomous Overcontroller": "You don't have to carry it all alone. Take a slow breath and let yourself be seen, even a little.",
  "Helpless Surrenderer": "You have more agency than it feels. Name one tiny thing you can choose right now, and take one steady breath.",
  "Self-Pity Victim": "Meet your pain with kindness, not resignation. Place a hand on your heart and offer yourself gentle care.",
  "Compliant Surrenderers": "Your yes means more when a no is possible. Quietly ask \u201CWhat do I want?\u201D and take one breath just for you."
};
function modeSoothe(mode) {
  if (!mode) return null;
  return MODE_SOOTHE[mode] ?? null;
}
const EMS_SCHEMAS = [
  // Domain I — Disconnection & Rejection
  { name: "Abandonment / Instability", domain: "Disconnection & Rejection", definition: "A fear that the people you rely on will leave or can't be depended on." },
  { name: "Mistrust / Abuse", domain: "Disconnection & Rejection", definition: "An expectation that others will hurt, use, or take advantage of you." },
  { name: "Emotional Deprivation", domain: "Disconnection & Rejection", definition: "A sense that your need for warmth, care and understanding won't be met." },
  { name: "Defectiveness / Shame", domain: "Disconnection & Rejection", definition: "A deep feeling of being flawed, bad, or unlovable if truly seen." },
  { name: "Social Isolation / Alienation", domain: "Disconnection & Rejection", definition: "Feeling different from others and like you don't belong anywhere." },
  // Domain II — Impaired Autonomy & Performance
  { name: "Dependence / Incompetence", domain: "Impaired Autonomy & Performance", definition: "Feeling unable to handle everyday life without a lot of help." },
  { name: "Vulnerability to Harm or Illness", domain: "Impaired Autonomy & Performance", definition: "An exaggerated fear that disaster could strike at any moment." },
  { name: "Enmeshment / Undeveloped Self", domain: "Impaired Autonomy & Performance", definition: "Being so close to others that you lose your own sense of identity." },
  { name: "Failure", domain: "Impaired Autonomy & Performance", definition: "A belief that you're bound to fail or fall short compared to others." },
  // Domain III — Impaired Limits
  { name: "Entitlement / Grandiosity", domain: "Impaired Limits", definition: "Feeling special or above the rules that apply to everyone else." },
  { name: "Insufficient Self-Control / Self-Discipline", domain: "Impaired Limits", definition: "Difficulty tolerating frustration or restraining impulses and feelings." },
  // Domain IV — Other-Directedness
  { name: "Subjugation", domain: "Other-Directedness", definition: "Surrendering control to others to avoid anger, conflict or abandonment." },
  { name: "Self-Sacrifice", domain: "Other-Directedness", definition: "Putting others' needs first to the point of neglecting your own." },
  { name: "Approval-Seeking / Recognition-Seeking", domain: "Other-Directedness", definition: "Basing your worth on others' approval rather than your true self." },
  // Domain V — Overvigilance & Inhibition
  { name: "Negativity / Pessimism", domain: "Overvigilance & Inhibition", definition: "Focusing on what could go wrong while discounting the good." },
  { name: "Emotional Inhibition", domain: "Overvigilance & Inhibition", definition: "Holding back feelings and spontaneity to avoid disapproval or losing control." },
  { name: "Unrelenting Standards / Hypercriticalness", domain: "Overvigilance & Inhibition", definition: "Feeling you must meet very high standards, often at the cost of ease." },
  { name: "Punitiveness", domain: "Overvigilance & Inhibition", definition: "Believing people \u2014 including you \u2014 should be harshly punished for mistakes." }
];
const EMS_DOMAINS = [
  "Disconnection & Rejection",
  "Impaired Autonomy & Performance",
  "Impaired Limits",
  "Other-Directedness",
  "Overvigilance & Inhibition"
];
const EMS_NAMES = EMS_SCHEMAS.map((s) => s.name);
function emsDefinition(name) {
  return EMS_SCHEMAS.find((s) => s.name === name)?.definition ?? null;
}
const EMS_SOOTHE = {
  "Abandonment / Instability": "Place a hand on your chest and remind yourself: right now, in this moment, you are here and you are okay. Name three steady things around you.",
  "Mistrust / Abuse": "Notice that you are safe in this moment. Feel your feet on the ground and take three slow breaths \u2014 you get to decide who is close to you.",
  "Emotional Deprivation": "Offer yourself the care you're longing for \u2014 a warm drink, a soft blanket, a hand on your heart. Your needs are valid.",
  "Defectiveness / Shame": "Speak to yourself as you would to someone you love: \u201CYou are worthy exactly as you are.\u201D Soften your face and breathe.",
  "Social Isolation / Alienation": "You belong to the human family. Bring to mind one person or place where you've felt even a little seen, and breathe into that.",
  "Dependence / Incompetence": "Recall one small thing you handled on your own recently. You are more capable than the fear says. Take one steady breath.",
  "Vulnerability to Harm or Illness": "Come back to now \u2014 the feared thing isn't happening this moment. Feel your feet, slow your exhale, and let your body know it's safe.",
  "Enmeshment / Undeveloped Self": "Gently ask yourself: \u201CWhat do I feel? What do I want?\u201D Place a hand on yourself \u2014 this is your own body, your own breath.",
  Failure: "Progress isn't perfection. Name one effort you made today; that counts. Unclench your hands and breathe out slowly.",
  "Entitlement / Grandiosity": "Pause and soften. Take a slow breath and notice one thing you appreciate about someone else \u2014 connection eases the pressure.",
  "Insufficient Self-Control / Self-Discipline": "Ride the urge like a wave \u2014 it will rise and pass. Breathe slowly and wait ten seconds before acting.",
  Subjugation: "Quietly claim: \u201CMy feelings and needs matter too.\u201D Take one breath that is just for you.",
  "Self-Sacrifice": "Turn some care back toward yourself. Ask \u201CWhat do I need right now?\u201D and let that be allowed.",
  "Approval-Seeking / Recognition-Seeking": "Your worth doesn't depend on anyone's applause. Place a hand on your heart and offer yourself the approval you seek.",
  "Negativity / Pessimism": "Let your body settle, then name one small thing that went okay today. Breathe slowly and widen the view a little.",
  "Emotional Inhibition": "It's safe to feel. Let your shoulders drop, unclench your jaw, and allow one honest breath and one honest feeling.",
  "Unrelenting Standards / Hypercriticalness": "Let \u201Cgood enough\u201D be enough right now. Loosen your shoulders and breathe out for a slow count of six.",
  Punitiveness: "Meet your mistake with mercy, not punishment. Say gently: \u201CI am allowed to be human,\u201D and soften your breath."
};
function emsSoothe(name) {
  return EMS_SOOTHE[name] ?? null;
}
const MOOD_LEVELS = [
  { value: 10, label: "Ecstatic", descriptor: "Ecstatic & fully expressed", color: "#15672E" },
  { value: 9, label: "Radiant", descriptor: "Radiant & expressive", color: "#257F3B" },
  { value: 8, label: "Empowered", descriptor: "Empowered & sovereign", color: "#34924B" },
  { value: 7, label: "Focused", descriptor: "Focused & productive", color: "#43A15F" },
  { value: 6, label: "Inspired", descriptor: "Inspired & curious", color: "#50AC77" },
  { value: 5, label: "Serene", descriptor: "Serene & meditative", color: "#5CB492" },
  { value: 4, label: "Harmonising", descriptor: "Harmonising & flowing", color: "#63B9AE" },
  { value: 3, label: "Relaxed", descriptor: "Relaxed & rested", color: "#6FBFC9" },
  { value: 2, label: "Content", descriptor: "Content & comfortable", color: "#83C8DF" },
  { value: 1, label: "Peaceful", descriptor: "Peaceful & quiet", color: "#9CD2EC" },
  { value: 0, label: "Grounded", descriptor: "Grounded baseline", color: "#C9C6AE" },
  { value: -1, label: "Heavy", descriptor: "Heavy & melancholic", color: "#ECD9A0" },
  { value: -2, label: "Flat", descriptor: "Flat & disengaged", color: "#EECE82" },
  { value: -3, label: "Self-critical", descriptor: "Self-critical & defeated", color: "#EFBF68" },
  { value: -4, label: "Isolated", descriptor: "Isolated & unseen", color: "#F0AE54" },
  { value: -5, label: "Drained", descriptor: "Drained & depleted", color: "#EE9C46" },
  { value: -6, label: "Anxious", descriptor: "Anxious & pressured", color: "#EB853F" },
  { value: -7, label: "Frustrated", descriptor: "Frustrated & overwhelmed", color: "#E56D3A" },
  { value: -8, label: "Defensive", descriptor: "Defensive & suspicious", color: "#DC5235" },
  { value: -9, label: "Agitated", descriptor: "Acute agitation & hostility", color: "#CD382F" },
  { value: -10, label: "Rage", descriptor: "Rage & panic", color: "#BB1E26" }
];
function moodLevel(value) {
  if (value == null) return null;
  return MOOD_LEVELS.find((m) => m.value === value) ?? null;
}
function moodColor(value) {
  return moodLevel(value)?.color ?? "#C9C6AE";
}
function moodLabel(value) {
  return moodLevel(value)?.label ?? null;
}
export {
  COPING_MODE_START,
  COPING_MODE_TREE,
  COPING_OPTIONS,
  DECISION_TREE,
  EMERGENCY_THRESHOLD,
  EMS_DOMAINS,
  EMS_NAMES,
  EMS_SCHEMAS,
  EMS_SOOTHE,
  FOURF_SOOTHE,
  FOUR_F,
  MODE_FOURF,
  MODE_MEANINGS,
  MODE_SOOTHE,
  MOOD_LEVELS,
  OVERRIDE_TOOL,
  SCHEMA_MEANINGS,
  SCHEMA_SOOTHE,
  SCHEMA_START,
  SCHEMA_TREE,
  SUDS_MAX,
  VALIDATION_TOOL,
  emsDefinition,
  emsSoothe,
  fourFConfig,
  fourFSoothe,
  isModeResult,
  isSchemaResult,
  modeFourF,
  modeMeaning,
  modeSoothe,
  moodColor,
  moodLabel,
  moodLevel,
  schemaMeaning,
  schemaSoothe,
  sudsColor,
  sudsLabel
};

